<template>
  <div class="referral-program-logs">
    <HeadSection
      :breadcrumbs="breadcrumbs"
      :permissionOfSection="permissionOfSection"
    />
    <MainContainer :loading="userLoading" :permissionOfSection="permissionOfSection">
      <div class="member-bets">
        <div class="d-flex flex-wrap justify-content-between align-items-center">
          <div class="d-flex flex-column">
            <h1>Activity Log</h1>
            <p>Logs provide a history of <b>referral program</b> activity.</p>
          </div>
          <a-tooltip
            placement="topLeft"
            arrow-point-at-center
            :get-popup-container="getPopupContainer"
            :overlayStyle="{'min-width': 'fit-content'}"
          >
            <template v-if="!data.length" slot="title">
              <span>The table is empty</span>
            </template>
            <a-button
              type="primary"
              size="large"
              :disabled="isLoading || !data.length"
              class="w-fit ml-4"
              @click="toggleExportModal(true)"
            >
              <a-icon type="upload" /> Export
            </a-button>
          </a-tooltip>
        </div>
        <div class="d-flex flex-wrap mb-3" style="gap: 20px;">
          <div class="d-flex flex-column">
            <span class="font-weight-bold">Date</span>
            <div class="d-flex align-items-center" style="gap: 5px;">
              <a-date-picker
                v-model="filters.date_start"
                format="YYYY-MM-DD HH:mm"
                :show-time="{
                  defaultValue: $moment('00:00:00', 'HH:mm'),
                  format: 'HH:mm'
                }"
                type="date"
                size="large"
                placeholder="Start date"
                :disabled="isLoading || !hasListData"
                @change="() => {resetPage(); getActivityLogs()}"
              />
              <span>-</span>
              <a-date-picker
                v-model="filters.date_end"
                format="YYYY-MM-DD HH:mm"
                :show-time="{
                  defaultValue: $moment('00:00:00', 'HH:mm'),
                  format: 'HH:mm'
                }"
                type="date"
                size="large"
                placeholder="End date"
                :disabled="isLoading || !hasListData"
                @change="() => {resetPage(); getActivityLogs()}"
              />
            </div>
          </div>
          <UniversalSelect
            v-model="filters.type"
            style="width: 150px;"
            :options="typeOptions"
            placeholder="Type"
            title="Type"
            :disabled="isLoading || !hasListData"
            @change="() => {resetPage(); getActivityLogs()}"
          />
          <div class="d-flex align-items-end" style="height: 62px;">
            <SearchField
              :value="filters.search"
              :loading="isLoading"
              :disabled="isLoading || !hasListData"
              @search="changeSearchValue"
            />
          </div>
          <div class="d-flex align-items-end">
            <a-button
              v-if="isFiltered"
              size="large"
              :disabled="isLoading"
              class="w-fit"
              @click="handleReset"
            >
              Reset filters
            </a-button>
          </div>
        </div>
        <a-table
          :dataSource="data"
          :pagination="meta"
          :columns="columns"
          :row-key="(record) => record.id"
          :loading="isLoading"
          @change="changePagination"
        >
          <template slot="created_at" slot-scope="created_at">
            <span>{{ created_at ? formatDate(created_at) : '-' }}</span>
          </template>
          <template slot="type" slot-scope="type">
            <span>{{ type ? getTypeName(type) : '-' }}</span>
          </template>
          <template slot="description" slot-scope="description">
            <InfoCell :info="description" />
          </template>
        </a-table>
        <TotalCount :count="meta.total" />
      </div>
    </MainContainer>
    <ExportModal
      v-if="showExportModal"
      :show="showExportModal"
      title="Export activity logs"
      text="A <b>.xlsx file</b> with logs, based on your table filters, will be sent to the specified <b>email address</b>."
      :loading="exportLoading"
      @confirm="exportLogs"
      @close="toggleExportModal(false)"
    />
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import UniversalSelect from '@/components/custom/filters/universalSelect.vue'
import SearchField from '@/components/custom/form/SearchField.vue'
import ExportModal from '@/views/apps/referral-program/referral-programs/components/ExportModal.vue'
import InfoCell from '@/components/custom/table/cells/InfoCell.vue'
import TotalCount from '@/views/apps/referral-program/components/TotalCount.vue'
import { getTableMeta } from '@/composables/useGetTableMeta.js'
import { getProgramParams } from '@/views/apps/referral-program/referral-programs/composables/useGetProgramParams.js'
import { setQueryParams, setFilters } from '@/composables/useSetQueryParams.js'
import { getFilledFilters } from '@/composables/useGetFilledFilters.js'
import { getErrorMessage } from '@/views/apps/fortune-wheel/composables/useGetErrorMessage.js'

const referralApiUrl = process.env.VUE_APP_REFERRAl_API_URL

export default {
  props: {
    editPermission: {
      type: Boolean,
      default: false,
    },
    deletePermission: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    HeadSection,
    MainContainer,
    UniversalSelect,
    SearchField,
    InfoCell,
    ExportModal,
    TotalCount,
  },
  data: () => ({
    permissionOfSection: 'referral_program',
    programId: null,
    program: null,
    data: [],
    meta: {
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      pageSizeOptions: ['10', '20', '40'],
    },
    filters: {
      page: undefined,
      per_page: undefined,
      date_start: undefined,
      date_end: undefined,
      type: undefined,
      search: undefined,
      sort_field: undefined,
      sort_dir: undefined,
    },
    showExportModal: false,
    exportLoading: false,
    initialLoading: false,
    loading: false,
    typeOptions: [
      { name: 'Member invite', value: 'member_invite' },
      { name: 'Member join', value: 'member_join' },
      { name: 'Member block', value: 'member_block' },
      { name: 'Member delete', value: 'member_delete' },
      { name: 'Account bet', value: 'account_bet' },
      { name: 'Cashback create', value: 'cashback_create' },
      { name: 'Cashback request', value: 'cashback_request' },
      { name: 'Cashback credit', value: 'cashback_credit' },
      { name: 'Cashback decline', value: 'cashback_decline' },
      { name: 'Program create', value: 'program_create' },
      { name: 'Program update', value: 'program_update' },
      { name: 'Program start', value: 'program_start' },
      { name: 'Program active', value: 'program_active' },
      { name: 'Program stopped', value: 'program_stopped' },
      { name: 'Program disabled', value: 'program_disabled' },
      { name: 'Program complete', value: 'program_complete' },
    ],
    columns: [
      {
        title: 'Date',
        dataIndex: 'created_at',
        width: '15%',
        ellipsis: true,
        scopedSlots: { customRender: 'created_at' },
        sorter: true,
      },
      {
        title: 'Type',
        dataIndex: 'type',
        width: '15%',
        scopedSlots: { customRender: 'type' },
      },
      {
        title: 'Initiator',
        dataIndex: 'initiator',
        width: '15%',
        scopedSlots: { customRender: 'initiator' },
      },
      {
        title: 'Login ID',
        dataIndex: 'login',
        width: '15%',
        scopedSlots: { customRender: 'login' },
      },
      {
        title: 'Description',
        dataIndex: 'description',
        width: '25%',
        scopedSlots: { customRender: 'description' },
      },
    ],
  }),
  computed: {
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    breadcrumbs() {
      return [
        { name: 'Referral programs', link: '/referral/programs' },
        { name: this.program?.name || '', link: `/referral/programs/${this.program?.id}` },
        { name: 'Logs', link: null },
      ]
    },
    isLoading() {
      return this.initialLoading || this.loading
    },
    isHasListItems() {
      return !!this.data?.length
    },
    isFiltered() {
      return Object.values(this.filters).some(v => v !== undefined && v !== null && v !== '')
    },
    hasListData() {
      return this.isHasListItems || this.isFiltered
    },
  },
  methods: {
    getPopupContainer() {
      return document.body
    },
    formatDate(date) {
      return this.$moment(date).tz(this.$moment.tz.guess()).format('DD.MM.YYYY HH:mm')
    },
    resetPage() {
      this.filters.page = 1
    },
    getTypeName(type) {
      const typeItem = this.typeOptions.find(item => item.value === type)
      if (!typeItem) {
        return '-'
      }
      return typeItem.name
    },
    toggleExportModal(status = false) {
      this.showExportModal = status
    },
    changeSearchValue(value) {
      this.filters.search = value
      this.resetPage()
      this.getActivityLogs()
    },
    changePagination(pagination, _, sorter) {
      this.filters.page = pagination.current
      this.filters.per_page = pagination.pageSize
      if (sorter && sorter?.order) {
        this.filters.sort_field = sorter.field
        this.filters.sort_dir = this.getSortDirectionParam(sorter.order)
      } else {
        this.filters.sort_field = undefined
        this.filters.sort_dir = undefined
      }
      this.getActivityLogs()
    },
    getFilters() {
      setQueryParams(this.filters, this, '', true)
      if (!this.isFiltered) {
        return {}
      }

      return getFilledFilters({ filters: this.filters, dateFields: ['date_start', 'date_end'] })
    },
    async getActivityLogs() {
      try {
        this.loading = true

        const filters = this.getFilters()

        const url = `${referralApiUrl}/referral-programs/${this.programId}/logs`
        const response = await apiClient.get(url, { params: filters })

        if (response?.data?.data) {
          this.data = response.data.data
          const newMeta = getTableMeta(response?.data?.meta)
          Object.assign(this.meta, newMeta)
          return
        }
        getErrorMessage(response?.data)
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.loading = false
      }
    },
    async getProgramParams() {
      const data = await getProgramParams(this.programId)

      if (data) {
        this.program = data
        return
      }
      this.$router.push('/referral/programs')
    },
    async exportLogs(email) {
      try {
        this.exportLoading = true

        const filters = this.getFilters()
        delete filters.page
        delete filters.per_page
        filters.email = email

        const url = `${referralApiUrl}/referral-programs/${this.programId}/logs/export`
        const response = await apiClient.get(url, { params: filters })

        if (response?.data?.data) {
          const message = response.data.data?.message || 'We`ve started exporting your program`s activity logs, and you`ll receive them by email shortly.'
          this.$notification.success({
            message,
          })
          this.toggleExportModal()
          return
        }
        getErrorMessage(response?.data)
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.exportLoading = false
      }
    },
    handleReset() {
      Object.keys(this.filters).forEach(key => {
        this.filters[key] = undefined
      })
      this.getActivityLogs()
    },
  },
  async mounted() {
    this.programId = this.$route.params.id

    if (Object.keys(this.$route.query)?.length) {
      setFilters(this.$route.query, this, '', true)
    }

    try {
      this.initialLoading = true
      const requests = [this.getProgramParams(), this.getActivityLogs()]

      await Promise.all(requests)
    } finally {
      this.$nextTick(() => {
        this.initialLoading = false
      })
    }
  },
}
</script>
